/**
 * The sticky dossier (prototype `person-page`, direction B): photo when a
 * Webflow profile has one, name, current office, the Webflow bio, and the
 * compact timeline navigation. No photo element at all without a profile.
 */
import type { PublicSeatAssignmentSection } from "../api";
import { h } from "../dom";
import { heldOfficeTitle, officeTitle, type TimelineEntry } from "../person-model";
import type { Profile } from "../webflow-content";

function roleLine(office: PublicSeatAssignmentSection | undefined): string | null {
  if (!office) return null;
  const title = heldOfficeTitle(office);
  const role = office.roleTitle?.trim();
  return role && !title.includes(role) ? `${role}, ${title}` : title;
}

export function entryTitle(entry: TimelineEntry): string {
  if (entry.kind === "office") return heldOfficeTitle(entry.office);
  const c = entry.candidacy;
  return c.race || c.body ? officeTitle(c) : c.cycle?.name ?? "Campaign";
}

export function renderDossier(opts: {
  name: string;
  profile: Profile | null;
  office: PublicSeatAssignmentSection | undefined;
  entries: TimelineEntry[];
  onSelect: (key: string) => void;
}): HTMLElement {
  const { name, profile } = opts;
  // Built here, from attributes, so the hidden Webflow list never downloads
  // a photo the visitor is not looking at.
  const photo = profile?.photoSrc
    ? h("figure", { class: "bsc-dossier__photo" }, h("img", { src: profile.photoSrc, alt: profile.photoAlt ?? `Portrait of ${name}`, decoding: "async" }))
    : null;
  const role = roleLine(opts.office);
  const firstName = name.split(/\s+/)[0];
  const nav = opts.entries.length
    ? h(
        "nav",
        { class: "bsc-timeline-nav", "aria-label": `${firstName}'s timeline` },
        h(
          "ol",
          null,
          opts.entries.map((entry, i) =>
            h(
              "li",
              { "data-kind": entry.kind, "data-outcome": entry.kind === "campaign" ? entry.outcome : null },
              h(
                "button",
                {
                  type: "button",
                  class: "bsc-timeline-nav__entry",
                  "data-target": entry.key,
                  "aria-current": i === 0 ? "true" : null,
                  onClick: () => opts.onSelect(entry.key),
                },
                // One line per part, as on the timeline; the spaces keep "2023 to Now" one phrase when read.
                h("span", { class: "bsc-timeline-nav__date" }, entry.dateParts.flatMap((part, i) => i ? [" ", h("span", null, part)] : [h("span", null, part)])),
                " ",
                h("span", { class: "bsc-timeline-nav__title" }, entryTitle(entry))
              )
            )
          )
        )
      )
    : null;
  return h(
    "aside",
    { class: `bsc-dossier${photo ? " bsc-dossier--has-photo" : ""}`, "aria-label": `About ${name}` },
    photo,
    h("h1", { class: "bsc-dossier__name" }, name),
    role ? h("p", { class: "bsc-dossier__role" }, role) : null,
    profile ? h("div", { class: "bsc-dossier__bio" }, profile.bio) : null,
    nav
  );
}
